import React, { useCallback, useState } from 'react';

import CursorListBody from '../shared/cursor_list_body';
import StatusPill from '../shared/status_pill';
import TpDialog from '../shared/tp_dialog';
import useCursorList from '../shared/use_cursor_list';
import { discard_guard_text } from '../shared/dialog_logic';
import {
  fetch_payout_claims,
  fetch_payout_deleted_accounts,
  fetch_payout_held,
  post_payout_claim_resolve
} from '../api';
import {
  CLAIM_STATUSES,
  REASON_MAX_LEN,
  claims_view,
  deleted_view,
  held_view,
  payout_error,
  payout_reason_error
} from './adapters';
import { claim_resolve_body } from './requests';
import LinesTable from './lines_table';
import ResolveDialog from './resolve_dialog';
import useIdempotentPost from './use_idempotent_post';

/*
 * PROD-7828 W4 — the queues that span months: held bank lines waiting for an Ops decision, the teachers'
 * "I haven't received this payment" reports, and deleted accounts with money still due (settled by a
 * payment outside the bank file from the teacher-month view).
 * Shared props: today, on_open_teacher(teacher_uuid, pay_month), on_notify(type, message), on_error(e, fallback).
 */

// ---- held lines ----

const read_held = (cursor) =>
  fetch_payout_held(cursor).then((d) => {
    const v = held_view(d);
    return { rows: v.rows, next: v.next_after_id };
  });

export const HeldPanel = ({ today, on_open_teacher, on_notify, on_error }) => {
  const list = useCursorList(read_held, 'Could not load the held lines.', on_error, payout_error);
  const [line, setLine] = useState(null);
  const [stale, setStale] = useState('');

  return (
    <div className="tp-po-held">
      <div className="tp-hint tp-po-intro">
        Bank lines the system could not settle on its own, oldest first. Check each one on the bank
        portal before deciding.
      </div>
      {stale ? (
        <div className="tp-banner tp-banner-warn" role="alert">
          {stale}
        </div>
      ) : null}
      <CursorListBody
        list={list}
        render_rows={(rows) => (
          <LinesTable
            rows={rows}
            on_open_teacher={on_open_teacher}
            action={(r) => (
              <button
                type="button"
                className="tp-link"
                aria-label={`Resolve held line ${r.line_no}`}
                onClick={() => setLine(r)}
              >
                Resolve
              </button>
            )}
            empty_text="No bank lines are waiting for Ops."
          />
        )}
      />
      {line ? (
        <ResolveDialog
          line={line}
          today={today}
          on_close={() => setLine(null)}
          on_done={(text) => {
            setLine(null);
            setStale('');
            on_notify('success', text);
            list.reload();
          }}
          on_stale={(text) => {
            setLine(null);
            setStale(text);
            list.reload();
          }}
          on_error={on_error}
        />
      ) : null}
    </div>
  );
};

// ---- claims ----

const ClaimResolveDialog = ({ claim, on_close, on_done, on_stale, on_error }) => {
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const failed = 'The report may not have been resolved. Reload the list to check.';
  const { busy, send } = useIdempotentPost(failed);
  const note_err = payout_reason_error(note, 'note');

  const submit = () => {
    const body = claim_resolve_body(claim.claim_uuid, note);
    send(body, () => post_payout_claim_resolve(body)).then((r) => {
      if (r.ok) {
        on_done(`Report from ${claim.teacher_name} resolved.`);
        return;
      }
      if (r.err.kind === 'auth') on_error(r.raw, failed);
      if (r.err.kind === 'stale') on_stale(r.err.text);
      setError(r.err.text);
      setConfirming(false);
    });
  };

  return (
    <TpDialog
      title="Resolve payment-not-received report"
      on_close={on_close}
      busy={busy}
      guard_text={discard_guard_text(note)}
    >
      <div className="tp-modal">
        <div className="tp-ctx">
          {claim.teacher_name} · {claim.pay_month} · {claim.amount} sent {claim.sent_on}, UTR{' '}
          <span className="tp-mono">{claim.bank_reference}</span>
          {claim.note ? <div>Teacher wrote: &quot;{claim.note}&quot;</div> : null}
        </div>
        <div className="tp-field">
          <label htmlFor="tp-po-claim-note">What you found (recorded)</label>
          <textarea
            id="tp-po-claim-note"
            className="tp-textarea"
            value={note}
            maxLength={REASON_MAX_LEN * 2}
            onChange={(e) => {
              setConfirming(false);
              setNote(e.target.value);
            }}
            onBlur={() => setTouched(true)}
            placeholder="For example: bank confirmed the UTR was credited on 8 Nov; shared it with the teacher."
          />
          {touched && note_err ? (
            <span className="tp-neg" role="alert">
              {note_err}
            </span>
          ) : null}
        </div>
        {confirming ? (
          <div className="tp-banner tp-banner-warn" role="alert">
            Mark this report resolved? The teacher sees it as resolved.
          </div>
        ) : null}
        {error ? (
          <div className="tp-neg" role="alert">
            {error}
          </div>
        ) : null}
        <div className="tp-modal-ft">
          <button type="button" className="tp-btn-ghost" onClick={busy ? undefined : on_close}>
            Cancel
          </button>
          {confirming ? (
            <button
              type="button"
              className="tp-btn"
              onClick={busy ? undefined : submit}
              aria-disabled={busy ? 'true' : undefined}
            >
              {busy ? 'Saving…' : 'Yes, resolve'}
            </button>
          ) : (
            <button
              type="button"
              className="tp-btn-out"
              onClick={() => {
                setTouched(true);
                if (!note_err) setConfirming(true);
              }}
            >
              Resolve…
            </button>
          )}
        </div>
      </div>
    </TpDialog>
  );
};

const claim_columns = (on_open_teacher, on_resolve) => [
  {
    key: 'teacher',
    label: 'Teacher',
    hideInCard: true,
    render: (c) => (
      <button
        type="button"
        className="tp-link"
        aria-label={`Open ${c.teacher_name}'s ${c.pay_month} payouts`}
        onClick={() => on_open_teacher(c.teacher_uuid, c.pay_month_raw)}
      >
        {c.teacher_name} · {c.pay_month}
      </button>
    )
  },
  { key: 'amount', label: 'Amount', align: 'right' },
  {
    key: 'sent',
    label: 'Sent',
    render: (c) => (
      <div>
        <div>{c.sent_on}</div>
        <div className="tp-hint tp-mono">UTR {c.bank_reference}</div>
      </div>
    )
  },
  {
    key: 'note',
    label: 'Teacher wrote',
    render: (c) => c.note || <span className="tp-hint">No note</span>
  },
  { key: 'created_at', label: 'Reported (IST)' },
  {
    key: 'status',
    label: 'Status',
    render: (c) => (
      <div>
        <StatusPill tone={c.status_view.tone}>{c.status_view.label}</StatusPill>
        {c.resolution_note ? (
          <div className="tp-hint">
            {c.resolution_note}
            {c.resolved_at ? ` (${c.resolved_at} IST)` : ''}
          </div>
        ) : null}
      </div>
    )
  },
  {
    key: 'action',
    label: 'Action',
    hideInCard: true,
    render: (c) =>
      c.status === 'OPEN' ? (
        <button
          type="button"
          className="tp-link"
          aria-label={`Resolve the report from ${c.teacher_name}`}
          onClick={() => on_resolve(c)}
        >
          Resolve
        </button>
      ) : null
  }
];

export const ClaimsPanel = ({ on_open_teacher, on_notify, on_error }) => {
  const [status, setStatus] = useState('OPEN');
  const [claim, setClaim] = useState(null);
  const [stale, setStale] = useState('');
  const read = useCallback(
    (cursor) =>
      fetch_payout_claims(status, cursor).then((d) => {
        const v = claims_view(d);
        return { rows: v.rows, next: v.next_after_id };
      }),
    [status]
  );
  const list = useCursorList(read, 'Could not load the reports.', on_error, payout_error);

  return (
    <div className="tp-po-claims-panel">
      <div className="tp-hint tp-po-intro">
        Teachers can report a sent payment as not received 2 bank working days after it was sent.
        Check the UTR with the bank, tell the teacher, then resolve.
      </div>
      <div className="tp-seg" role="group" aria-label="Report status">
        {CLAIM_STATUSES.map((s) => (
          <button
            key={s.value}
            type="button"
            className={status === s.value ? 'on' : ''}
            aria-pressed={status === s.value}
            onClick={() => setStatus(s.value)}
          >
            {s.label}
          </button>
        ))}
      </div>
      {stale ? (
        <div className="tp-banner tp-banner-warn" role="alert">
          {stale}
        </div>
      ) : null}
      <CursorListBody
        list={list}
        columns={claim_columns(on_open_teacher, setClaim)}
        rowKey={(c) => c.claim_uuid}
        empty_text="No reports."
        title_of={(c) => `${c.teacher_name} · ${c.pay_month}`}
      />
      {claim ? (
        <ClaimResolveDialog
          claim={claim}
          on_close={() => setClaim(null)}
          on_done={(text) => {
            setClaim(null);
            setStale('');
            on_notify('success', text);
            list.reload();
          }}
          on_stale={(text) => {
            setClaim(null);
            setStale(text);
            list.reload();
          }}
          on_error={on_error}
        />
      ) : null}
    </div>
  );
};

// ---- deleted accounts ----

const read_deleted = (cursor) =>
  fetch_payout_deleted_accounts(cursor).then((d) => {
    const v = deleted_view(d);
    return { rows: v.rows, next: v.next_after_teacher_id };
  });

const deleted_columns = (on_open_teacher) => [
  { key: 'teacher_label', label: 'Teacher', hideInCard: true },
  { key: 'total', label: 'Total due', align: 'right' },
  { key: 'account', label: 'Last approved account' },
  {
    key: 'months',
    label: 'Months',
    render: (r) => (
      <ul className="tp-po-months">
        {r.months.map((m) => (
          <li key={m.pay_month}>
            <button
              type="button"
              className="tp-link"
              aria-label={`Open ${r.teacher_label}'s ${m.pay_month} payouts`}
              onClick={() => on_open_teacher(r.teacher_uuid, m.pay_month)}
            >
              {m.pay_month}
            </button>{' '}
            {m.outstanding} <span className="tp-hint">({m.lifecycle})</span>
          </li>
        ))}
      </ul>
    )
  }
];

export const DeletedPanel = ({ on_open_teacher, on_error }) => {
  const list = useCursorList(
    read_deleted,
    'Could not load the deleted accounts.',
    on_error,
    payout_error
  );
  return (
    <div className="tp-po-deleted">
      <div className="tp-hint tp-po-intro">
        Teachers who deleted their account while money is still due. They are never put in a bank
        file: pay the last approved account outside the file, then open the month and record the
        payment.
      </div>
      <CursorListBody
        list={list}
        columns={deleted_columns(on_open_teacher)}
        rowKey={(r) => r.teacher_uuid}
        empty_text="No deleted account has money due."
        title_of={(r) => r.teacher_label}
      />
    </div>
  );
};
